/**
 * 处理添加购物车操作
 */
if (!customElements.get("product-form")) {
  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector("form");
        if (!this.form) return;

        this.isloading = false;
        this.error = false;

        this.variantIdInput.disabled = false;
        this.form.addEventListener(
          "submit",
          this.onSubmitHandler.bind(this),
        );

        // 只获取真正的“加入购物车”按钮，避免误处理动态结账按钮。
        this.addCartButtons = this.querySelectorAll(
          'button[type="submit"][name="add"]',
        );

        this.cartDrawer = document.getElementById("Cart-Drawer");

        if (
          this.cartDrawer &&
          !this.cartDrawer.hasAttribute("data-status-silence")
        ) {
          this.addCartButtons.forEach((button) => {
            button.setAttribute("aria-haspopup", "dialog");
            button.setAttribute("aria-expanded", "false");
            button.setAttribute("aria-controls", "Cart-Drawer");
          });
        }

        this.errorMessageWrapper = document.getElementById(
          `Product-Form-Error-Message-${this.dataset.section}`,
        );

        // 快速购物弹窗。
        this.quickAddDrawer = this.closest("product-side-drawer");

        // 移动端/桌面端迷你加购栏。
        this.miniChecker = document.getElementById(
          `Product-Mini-Checkout-${this.dataset.section}`,
        );

        // 只观察页面中原始的主加购按钮。
        // 当主加购按钮从页面顶部滚出后，才显示悬浮加购栏。
        this.mainAddCartButton = this.querySelector(".add-cart-button");

        if (this.miniChecker && this.mainAddCartButton) {
          this.miniCheckerObserver = new IntersectionObserver(
            (entries) => {
              const entry = entries[0];
              if (!entry) return;

              if (entry.isIntersecting) {
                this.hideMiniCheck();
                return;
              }

              // 按钮从顶部滚出视口后显示。
              // 按钮仍在视口下方时保持隐藏，避免页面刚打开就出现。
              if (entry.boundingClientRect.bottom < 0) {
                this.showMiniCheck();
              } else {
                this.hideMiniCheck();
              }
            },
            {
              root: null,
              threshold: 0,
              rootMargin: "0px",
            },
          );

          this.miniCheckerObserver.observe(this.mainAddCartButton);
        }
      }

      disconnectedCallback() {
        if (this.miniCheckerObserver) {
          this.miniCheckerObserver.disconnect();
        }
      }

      get variantIdInput() {
        return this.form.querySelector("[name=id]");
      }

      /**
       * 是否隐藏错误信息。
       * 当产品是礼品卡的时候，隐藏错误信息。
       * @returns {boolean}
       */
      get hideErrors() {
        return this.dataset.hideErrors === "true";
      }

      /**
       * 处理点击提交购物车。
       * @param {SubmitEvent} event
       */
      onSubmitHandler(event) {
        if (this.isloading) return;

        event.preventDefault();

        if (event.submitter?.hasAttribute("disabled")) return;

        this.handleErrorMessage();
        this.startLoading();

        const config = webvista.fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";

        // 浏览器会自动为 FormData 生成包含 boundary 的 multipart/form-data。
        delete config.headers["Content-Type"];

        const formData = new FormData(this.form);

        formData.append(
          "sections",
          this.getSectionsToRender()
            .map((section) => section.section)
            .join(),
        );

        formData.append("sections_url", window.location.pathname);
        config.body = formData;

        fetch(`${window["routes"]["cart_add_url"]}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              webvista.publish(PUB_SUB_EVENTS.cartError, {
                source: "product-form",
                productVariantId: formData.get("id"),
                errors: response.errors || response.description,
                message: response.message,
              });

              this.handleErrorMessage(response.description);
              this.error = true;
              return;
            }

            this.error = false;

            // 动态更新 Sections 内容。
            SectionDynamicUpdate.updateSections(
              this.getSectionsToRender(),
              response.sections,
            );

            webvista.publish(PUB_SUB_EVENTS.cartUpdate, {
              source: "product-form",
              productVariantId: formData.get("id"),
              cartData: response,
            });

            if (
              this.cartDrawer &&
              !this.cartDrawer.hasAttribute("data-status-silence")
            ) {
              this.cartDrawer.show(event.submitter);
            } else {
              window.location = window["routes"]["cart_url"];
            }
          })
          .catch(() => {
            this.handleErrorMessage(
              window["accessibilityStrings"]["unknownError"],
            );
            this.error = true;
          })
          .finally(() => {
            this.endLoading();

            // 成功加入购物车后关闭悬浮栏。
            // 发生错误时保留悬浮栏，方便用户再次尝试。
            if (!this.error) {
              this.hideMiniCheck();
            }
          });
      }

      /**
       * 获取需要重新渲染的位置。
       * @returns {[{section: string, selector: string, id: string}]}
       */
      getSectionsToRender() {
        const sections = [
          {
            id: "Cart-Icon-Bubble",
            section: "cart-icon-bubble",
            selector: ".shopify-section",
          },
        ];

        if (
          this.cartDrawer &&
          !this.cartDrawer.hasAttribute("data-status-silence")
        ) {
          sections.push({
            id: "Cart-Drawer",
            section: this.cartDrawer.dataset.section,
            selector: "#Cart-Drawer-Details",
          });
        }

        return sections;
      }

      /**
       * 错误显示。
       * @param {string|null} errorMessage
       */
      handleErrorMessage(errorMessage = null) {
        if (this.hideErrors || !this.errorMessageWrapper) return;

        this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage);
        this.errorMessageWrapper.textContent = errorMessage || "";
      }

      startLoading() {
        this.isloading = true;

        this.addCartButtons.forEach((button) => {
          button.setAttribute("disabled", "disabled");
          button.classList.add("loading");
        });
      }

      endLoading() {
        this.isloading = false;

        this.addCartButtons.forEach((button) => {
          button.removeAttribute("disabled");
          button.classList.remove("loading");
        });

        // 关闭快速预览。
        if (!this.error && this.quickAddDrawer) {
          this.quickAddDrawer.hide();
        }
      }

      showMiniCheck() {
        if (!this.miniChecker) return;

        this.miniChecker.classList.add("active");
        this.miniChecker.setAttribute("aria-hidden", "false");
      }

      hideMiniCheck() {
        if (!this.miniChecker) return;

        this.miniChecker.classList.remove("active");
        this.miniChecker.setAttribute("aria-hidden", "true");
      }
    },
  );
}
